import Select, { components } from "react-select";

// Rounded-pill react-select multi-select used by the support ticket filters.
// The control stays a fixed-height single line — it shows the placeholder
// (e.g. "All Statuses") when nothing is picked and "2 selected" otherwise,
// while the menu options get checkboxes. `value` is an array of option
// values; an empty selection means "no filter".

const Option = (props) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2.5">
      <div
        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-2 ${
          props.isSelected ? "border-[#2B3674] bg-[#2B3674]" : "border-gray-300 bg-white"
        }`}
      >
        {props.isSelected && (
          <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6l3 3 5-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span>{props.label}</span>
    </div>
  </components.Option>
);

// children is [selected values / placeholder, input] — the values are
// replaced with a short summary, the (hidden) input must stay for focus.
const ValueContainer = ({ children, ...props }) => {
  const [, input] = children;
  const count = props.getValue().length;

  return (
    <components.ValueContainer {...props}>
      <span className={count ? "text-[#14122B] font-medium" : "text-[#4B5266] font-medium"}>
        {count ? `${count} selected` : props.selectProps.placeholder}
      </span>
      {input}
    </components.ValueContainer>
  );
};

const styles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    borderColor: state.isFocused ? "rgba(139,92,246,0.5)" : "#ECEEF4",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(139,92,246,0.1)" : "none",
    "&:hover": { borderColor: state.isFocused ? "rgba(139,92,246,0.5)" : "#D9DCE6" },
    height: "44px",
    paddingLeft: "8px",
    fontSize: "0.875rem",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#9AA1B2", paddingRight: "12px" }),
  // react-select switches this to display:grid when nothing is selected,
  // which misplaces our custom summary text — force a single-line flexbox.
  valueContainer: (base) => ({
    ...base,
    display: "flex",
    height: "100%",
    flexWrap: "nowrap",
    alignItems: "center",
    overflow: "hidden",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    border: "1px solid #ECEEF4",
    boxShadow: "0 8px 24px rgba(20,18,43,0.08)",
    overflow: "hidden",
    fontSize: "0.875rem",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#F5F2FF" : "white",
    color: "#14122B",
    cursor: "pointer",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
};

const FilterMultiSelect = ({ options, value, onChange, placeholder }) => (
  <Select
    isMulti
    isSearchable={false}
    options={options}
    value={options.filter((option) => value.includes(option.value))}
    onChange={(selected) => onChange((selected ?? []).map((option) => option.value))}
    placeholder={placeholder}
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    components={{ Option, ValueContainer }}
    classNamePrefix="react-select"
    styles={styles}
    menuPortalTarget={document.body}
  />
);

export default FilterMultiSelect;
